import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import PeriodFilter, { type PeriodRange } from "@components/PeriodFilter";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type AutoreadRow, useAutoread } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Radio, Select, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

const measurementOptions = [
  { label: "A+", value: 1 },
  { label: "A-", value: 2 },
  { label: "R+", value: 3 },
  { label: "R-", value: 4 },
];

const formatValue = (value?: number, digits = 3) => {
  if (value === undefined || value === null) {
    return "—";
  }
  return value.toFixed(digits);
};

const ReportsAutoreadPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [measurement, setMeasurement] = useState<number>(1);
  const [showTariffs, setShowTariffs] = useState(false);
  const [period, setPeriod] = useState<PeriodRange>(() => {
    const start = dayjs().startOf("day");
    return { from: start, to: start.add(1, "day") };
  });

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;
  // Отправляем день без часового пояса, чтобы не сдвигать дату на -1
  const dateISO = period.from?.format("YYYY-MM-DD");

  const autoreadQuery = useAutoread({
    region: regionID,
    object: objectID,
    date: dateISO,
    measurement,
  });
  const data = autoreadQuery.data ?? [];
  const pagination = useStandardPagination(data.length);

  const columns: ColumnsType<AutoreadRow> = useMemo(() => {
    const base: ColumnsType<AutoreadRow> = [
      { title: "№ фидера", dataIndex: "feeder_id", width: 110 },
      {
        title: "Фидер",
        dataIndex: "feeder_name",
        ellipsis: true,
        render: (value: string, row) => row.feeder_dispatch ?? value,
      },
      {
        title: "Счётчик №",
        dataIndex: "meter_number",
        width: 140,
        render: (value?: number) => value ?? "—",
      },
      { title: "Название канала", dataIndex: "channel_label", ellipsis: true },
      { title: "Измерение", dataIndex: "measurement_label", width: 120 },
      {
        title: "Дата чтения",
        dataIndex: "reading_time",
        width: 180,
        render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm"),
      },
    ];
    if (showTariffs) {
      base.push(
        {
          title: "Тариф 1",
          dataIndex: "tariff1",
          width: 120,
          render: (v?: number) => formatValue(v),
        },
        {
          title: "Тариф 2",
          dataIndex: "tariff2",
          width: 120,
          render: (v?: number) => formatValue(v),
        },
        {
          title: "Тариф 3",
          dataIndex: "tariff3",
          width: 120,
          render: (v?: number) => formatValue(v),
        },
        {
          title: "Тариф 4",
          dataIndex: "tariff4",
          width: 120,
          render: (v?: number) => formatValue(v),
        },
      );
    }
    base.push(
      { title: "Итого", dataIndex: "total", width: 140, render: (v?: number) => formatValue(v) },
      {
        title: "Ct × Vt × M",
        dataIndex: "transformer_ratio",
        width: 140,
        render: (v: number) => formatValue(v, 4),
      },
    );
    return base;
  }, [showTariffs]);

  const ready = regionID !== undefined && objectID !== undefined && dateISO !== undefined;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/autoread", {
        region: regionID,
        object: objectID,
        date: dateISO,
        measurement,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const measurementLabel =
    measurementOptions.find((option) => option.value === measurement)?.label ?? `№${measurement}`;
  const dateLabel = period.from ? period.from.format("DD.MM.YYYY") : "—";

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    openComposer({
      subject: `Суточные показания — ${objectLabel}`,
      body: `Отчёт: Суточные показания\nОбъект: ${objectLabel}\nДата: ${dateLabel}\nПоказатель: ${measurementLabel}\n\nExcel-файл находится во вложении.`,
      filename: `autoread_${regionID ?? "r"}_${objectID ?? "obj"}_${period.from?.format("YYYYMMDD") ?? "date"}.xlsx`,
    });
  }, [
    ready,
    currentObject,
    objectID,
    regionID,
    dateLabel,
    measurementLabel,
    openComposer,
    period.from,
  ]);

  return (
    <PageLayoutWithHelp
      title="Суточные показания по объекту"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              style={{ minWidth: 320, flex: 1 }}
            />
            <Select
              options={measurementOptions}
              value={measurement}
              onChange={(value) => setMeasurement(value)}
              style={{ width: 160 }}
            />
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={showTariffs}
              onChange={(e) => setShowTariffs(e.target.value)}
              options={[
                { label: "Сумма", value: false },
                { label: "По тарифам", value: true },
              ]}
            />
          </FilterRow>
          <PeriodFilter
            value={period}
            onChange={setPeriod}
            showMonthControls={false}
            showRangeControls={false}
          />
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title="Выберите объект и дату, чтобы увидеть суточные показания."
            style={{ maxWidth: 600 }}
          />
        )}
        {ready && autoreadQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Ошибка загрузки данных"
            description="Не удалось загрузить суточные показания. Попробуйте обновить страницу или выбрать другую дату."
          />
        )}
        {ready && (
          <EnterpriseTable<AutoreadRow>
            rowKey={(row) => `${row.feeder_id}-${row.meter_number ?? row.channel_label}`}
            columns={columns}
            dataSource={data}
            loading={autoreadQuery.isLoading}
            pagination={pagination}
            scroll={{ x: 1200 }}
            size="small"
          />
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsAutoreadPage;
